import { useState, useEffect, useRef } from "react";
import EnhancedTable from "components/utils/Table.js";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import PatientFormService from "services/PatientFormService.js";
import FamilyService from "services/FamilyService.js";
import { useNavigate } from "react-router-dom";
import useAPI from "hooks/useAPI.js";
import DateUtils from "utils/DateUtils.js";

function RegistrationForms() {
  const navigate = useNavigate();
  const [intakePacksList, setIntakePacksList] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showIntakePacks, setShowIntakePacks] = useState(false);
  const selectedPatientIdRef = useRef(null);

  const getMembersAPI = useAPI(
    FamilyService.getMembers,
    getMembersHandler,
    false,
    true
  );

  const getIntakePacksAPI = useAPI(
    PatientFormService.getIntakePacks,
    getIntakePacksHandler
  );

  useEffect(() => {
    // Load family members on component mount
    getMembersAPI.execute();
  }, []);

  function getMembersHandler(response) {
    setFamilyMembers(response.data || []);
  }

  function getIntakePacksHandler(response) {
    const parseCreatedAt = (value) => {
      const time = value ? new Date(value).getTime() : 0;
      return Number.isNaN(time) ? 0 : time;
    };

    const sortedPacks = [...response.data].sort(
      (a, b) => parseCreatedAt(b.createdAt) - parseCreatedAt(a.createdAt)
    );

    const transformedData = sortedPacks.map((intakePack, index) =>
      transformJsonData(intakePack, index === 0)
    );

    const dataWithLatestChip = transformedData.map((row) =>
      row.isLatest
        ? {
            ...row,
            packageName: (
              <>
                {row.packageName}
                {" "}
                <Chip
                  label="Latest"
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              </>
            ),
          }
        : row
    );

    setIntakePacksList(dataWithLatestChip);
  }

  function handleIntakePackRowClick(row) {
    if (!selectedPatient?.id) {
      return;
    }
    navigate(`/patient/${selectedPatient.id}/intakes/${row.id}`);
  }

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    selectedPatientIdRef.current = patient.id;
    setShowIntakePacks(true);
    getIntakePacksAPI.execute(patient.id);
  };

  const handleBackToPatients = () => {
    setSelectedPatient(null);
    selectedPatientIdRef.current = null;
    setShowIntakePacks(false);
    setIntakePacksList([]);
  };

  return (
    <div className="registration-forms-container page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Registration Forms</h1>
          <p className="page-subtitle">
            Select a patient and complete required forms before upcoming visits.
          </p>
        </div>
      </div>

      {!showIntakePacks ? (
        // Show patient list
        <Paper elevation={0} className="surface-card section-card">
          {getMembersAPI.status === "pending" ? (
            <Grid container spacing={2}>
              {[1, 2, 3].map((idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Skeleton variant="rounded" height={78} />
                </Grid>
              ))}
            </Grid>
          ) : familyMembers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Typography variant="h6">No patient profiles yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add a family member profile to begin registration.
              </Typography>
            </Box>
          ) : (
            <div>
              <Typography variant="h4" className="mb-3">
                Select a patient to view intake bundles:
              </Typography>
              <Grid container spacing={2}>
                {familyMembers.map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member.id}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handlePatientClick(member)}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        p: 2,
                        borderRadius: 3,
                        textTransform: "none",
                        borderColor: "divider",
                        "&:hover": {
                          borderColor: "primary.main",
                          backgroundColor: "primary.light",
                        },
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <Typography variant="subtitle1" noWrap>
                          {member.firstName} {member.lastName}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          DOB: {member.dob}
                        </Typography>
                      </div>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </div>
          )}
        </Paper>
      ) : (
        // Show intake packs for selected patient
        <Paper elevation={0} className="surface-card section-card">
          <div className="mb-3">
            <Button
              variant="text"
              onClick={handleBackToPatients}
              sx={{ mb: 2 }}
            >
              ← Back to Patients
            </Button>
            <Typography variant="h4">
              Intake Bundles for {selectedPatient?.firstName} {selectedPatient?.lastName}
            </Typography>
          </div>

          {getIntakePacksAPI.status === "pending" ? (
            <div className="text-center">
              <CircularProgress className="text-center" sx={{ my: 20 }} />
            </div>
          ) : intakePacksList.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, border: "1px dashed", borderColor: "divider", borderRadius: 2 }}>
              <Typography variant="h6">No intake bundles available yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Your clinic has not assigned forms for this patient yet. Please check again later.
              </Typography>
            </Box>
          ) : (
            <EnhancedTable
              headings={tableHeadings}
              rowsData={intakePacksList}
              rowsPerPage={10}
              onRowClick={handleIntakePackRowClick}
            />
          )}
        </Paper>
      )}

      <p className="inline-note">
        *Click a row to open the intake bundle and complete related forms.
      </p>
    </div>
  );
}

const tableHeadings = [
  {
    id: "id",
    label: "ID",
  },
  {
    id: "packageName",
    label: "Intake Bundle",
  },
  {
    id: "description",
    label: "Description",
  },
  {
    id: "createdAtDisplay",
    label: "Created At",
  },
];

function transformJsonData(intakePack, isLatest = false) {
  return {
    id: intakePack.id,
    packageName: intakePack.packageName,
    description: intakePack.description,
    createdAtDisplay: DateUtils.formatDateTime(intakePack.createdAt),
    documentLink: true,
    isLatest,
  };
}

export default RegistrationForms;